import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  CircleDollarSign,
  Download,
  FileBarChart2,
  Loader2,
  Package,
  Search,
  Warehouse as WarehouseIcon,
} from "lucide-react";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useExistenciasReport,
  useKardexReport,
  useValorizacionReport,
  useWarehouses,
} from "@/lib/api/inventory.queries";
import { useProducts } from "@/lib/api/products.queries";
import { useActiveCompany } from "@/lib/company-context";
import { StatCard } from "@/components/inventory/StatCard";
import { MovementKindBadge } from "@/components/inventory/MovementKindBadge";
import { fmtDateTime, fmtQty } from "@/components/inventory/format";

export const Route = createFileRoute(
  "/companies/$companyId/inventario/reportes",
)({
  component: ReportesPage,
});

function ReportesPage() {
  return (
    <RequireAuth>
      <AppShell>
        <ReportesContent />
      </AppShell>
    </RequireAuth>
  );
}

function ReportesContent() {
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6">
      <header className="space-y-1.5">
        <Link
          to="/companies/$companyId/inventario"
          params={{ companyId }}
          className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          ← Volver a Inventario
        </Link>
        <div className="flex items-center gap-2">
          <FileBarChart2 className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-semibold tracking-tight">
            Reportes de inventario
          </h1>
        </div>
        <p className="max-w-2xl text-sm text-muted-foreground">
          Existencias al corte (en vivo o histórico), valorización por bodega y
          cuenta del PUC, y kardex por producto. Todos los reportes son
          coherentes con los asientos contables ya posteados.
        </p>
      </header>

      <Tabs defaultValue="existencias">
        <TabsList>
          <TabsTrigger value="existencias">Existencias</TabsTrigger>
          <TabsTrigger value="valorizacion">Valorización</TabsTrigger>
          <TabsTrigger value="kardex">Kardex por producto</TabsTrigger>
        </TabsList>
        <TabsContent value="existencias" className="mt-4">
          <ExistenciasTab companyId={companyId} />
        </TabsContent>
        <TabsContent value="valorizacion" className="mt-4">
          <ValorizacionTab companyId={companyId} />
        </TabsContent>
        <TabsContent value="kardex" className="mt-4">
          <KardexTab companyId={companyId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Existencias ───────────────────────────────────────────────────────────

function ExistenciasTab({ companyId }: { companyId: string }) {
  const fmtCop = useMoneyFormatter();
  const fmtCop2 = useMoneyFormatter();
  const [asOf, setAsOf] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const warehousesQuery = useWarehouses(companyId);
  const reportQuery = useExistenciasReport(companyId, {
    asOf: asOf ? `${asOf}T23:59:59Z` : undefined,
    warehouseId: warehouseId || undefined,
  });

  function downloadCsv() {
    const data = reportQuery.data;
    if (!data) return;
    const header = [
      "Producto",
      "SKU",
      "Bodega",
      "Cantidad",
      "Costo promedio",
      "Valor",
    ];
    const rows = data.rows.map((r) => [
      r.productName,
      r.productSku ?? "",
      r.warehouseCode,
      r.qty.toString(),
      r.avgUnitCost.toString(),
      r.totalValue.toString(),
    ]);
    const csv = [header, ...rows]
      .map((r) =>
        r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `existencias-${asOf || "live"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="grid gap-3 py-4 sm:grid-cols-[1fr_1fr_auto]">
          <div>
            <Label>Fecha de corte</Label>
            <Input
              type="date"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Vacío = saldos en vivo. Una fecha histórica reproduce el saldo a
              partir del kardex.
            </p>
          </div>
          <div>
            <Label>Bodega</Label>
            <select
              className="mt-1 w-full rounded border bg-background px-2 py-2 text-sm"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
            >
              <option value="">Todas</option>
              {(warehousesQuery.data?.warehouses ?? []).map((w) => (
                <option key={w.id} value={w.id}>
                  {w.code} — {w.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              size="sm"
              disabled={!reportQuery.data || reportQuery.data.rows.length === 0}
              onClick={downloadCsv}
            >
              <Download className="h-4 w-4" />
              CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {reportQuery.data && (
        <div className="grid gap-3 sm:grid-cols-3">
          <StatCard
            label="Productos"
            value={fmtQty(reportQuery.data.totals.productCount)}
            icon={<Package className="h-4 w-4" />}
          />
          <StatCard
            label="Bodegas"
            value={fmtQty(reportQuery.data.totals.warehouseCount)}
            icon={<WarehouseIcon className="h-4 w-4" />}
          />
          <StatCard
            label="Valor total"
            value={fmtCop(reportQuery.data.totals.totalValue)}
            icon={<CircleDollarSign className="h-4 w-4" />}
            tone="primary"
          />
        </div>
      )}

      {reportQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : reportQuery.isError ? (
        <ErrorState
          title="No pudimos generar el reporte"
          description={reportQuery.error?.message ?? ""}
          onRetry={() => void reportQuery.refetch()}
          retryLabel="Reintentar"
        />
      ) : reportQuery.data!.rows.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Sin existencias para los filtros seleccionados.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Producto</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Bodega</TableHead>
                <TableHead className="text-right">Cantidad</TableHead>
                <TableHead className="text-right">Costo prom.</TableHead>
                <TableHead className="text-right">Valor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reportQuery.data!.rows.map((r) => (
                <TableRow key={`${r.productId}:${r.warehouseId}`}>
                  <TableCell>
                    <Link
                      to="/companies/$companyId/productos/$productId"
                      params={{ companyId, productId: r.productId }}
                      className="font-medium hover:underline"
                    >
                      {r.productName}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {r.productSku ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant="muted" className="font-mono">
                      {r.warehouseCode}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmtQty(r.qty)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {fmtCop2(r.avgUnitCost)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {fmtCop(r.totalValue)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

// ── Valorización ──────────────────────────────────────────────────────────

function ValorizacionTab({ companyId }: { companyId: string }) {
  const fmtCop = useMoneyFormatter();
  const [asOf, setAsOf] = useState("");
  const reportQuery = useValorizacionReport(companyId, {
    asOf: asOf ? `${asOf}T23:59:59Z` : undefined,
  });

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="py-4">
          <Label>Fecha de corte</Label>
          <Input
            type="date"
            className="max-w-xs"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
          />
        </CardContent>
      </Card>

      {reportQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : reportQuery.isError ? (
        <ErrorState
          title="No pudimos generar el reporte"
          description={reportQuery.error?.message ?? ""}
          onRetry={() => void reportQuery.refetch()}
          retryLabel="Reintentar"
        />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <StatCard
              label="Valor total"
              value={fmtCop(reportQuery.data!.totals.totalValue)}
              icon={<CircleDollarSign className="h-4 w-4" />}
              tone="primary"
            />
            <StatCard
              label="Productos con stock"
              value={fmtQty(reportQuery.data!.totals.productCount)}
              icon={<Package className="h-4 w-4" />}
            />
            <StatCard
              label="Bodegas"
              value={fmtQty(reportQuery.data!.totals.warehouseCount)}
              icon={<WarehouseIcon className="h-4 w-4" />}
            />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <div className="border-b px-4 py-3">
                <h3 className="text-sm font-semibold">Por bodega</h3>
                <p className="text-xs text-muted-foreground">
                  Distribución del valor de inventario por ubicación.
                </p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bodega</TableHead>
                    <TableHead className="text-right">Productos</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportQuery.data!.byWarehouse.map((r) => (
                    <TableRow key={r.warehouseId}>
                      <TableCell>
                        <Badge variant="muted" className="font-mono">
                          {r.warehouseCode}
                        </Badge>
                        <span className="ml-2 text-sm">{r.warehouseName}</span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtQty(r.productCount)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {fmtCop(r.totalValue)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
            <Card>
              <div className="border-b px-4 py-3">
                <h3 className="text-sm font-semibold">Por cuenta del PUC</h3>
                <p className="text-xs text-muted-foreground">
                  Agregado para cuadrar la sección 14XX del balance.
                </p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Cuenta</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportQuery.data!.byAccount.map((r) => (
                    <TableRow key={r.accountCode}>
                      <TableCell className="font-mono text-sm">
                        {r.accountCode}
                      </TableCell>
                      <TableCell>{r.accountName}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {fmtCop(r.totalValue)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

// ── Kardex ────────────────────────────────────────────────────────────────

function KardexTab({ companyId }: { companyId: string }) {
  const fmtCop = useMoneyFormatter();
  const fmtCop2 = useMoneyFormatter();
  const [productId, setProductId] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const productsQuery = useProducts(companyId, { pageSize: 200 });
  const warehousesQuery = useWarehouses(companyId);
  const reportQuery = useKardexReport(companyId, {
    productId: productId || undefined,
    warehouseId: warehouseId || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo ? `${dateTo}T23:59:59Z` : undefined,
  });

  const filteredProducts = useMemo(() => {
    const all = (productsQuery.data?.products ?? []).filter(
      (p) => p.kind === "product",
    );
    if (!productSearch.trim()) return all.slice(0, 50);
    const needle = productSearch.trim().toLowerCase();
    return all
      .filter(
        (p) =>
          p.name.toLowerCase().includes(needle) ||
          p.sku?.toLowerCase().includes(needle),
      )
      .slice(0, 50);
  }, [productsQuery.data, productSearch]);

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="grid gap-3 py-4 lg:grid-cols-[2fr_1fr_1fr_1fr]">
          <div>
            <Label>Producto</Label>
            <div className="relative mt-1">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Buscar..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
              />
            </div>
            <select
              className="mt-2 w-full rounded border bg-background px-2 py-2 text-sm"
              size={4}
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
            >
              {filteredProducts.length === 0 && (
                <option disabled value="">
                  Sin resultados
                </option>
              )}
              {filteredProducts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.sku ? ` · ${p.sku}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Bodega</Label>
            <select
              className="mt-1 w-full rounded border bg-background px-2 py-2 text-sm"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
            >
              <option value="">Todas</option>
              {(warehousesQuery.data?.warehouses ?? []).map((w) => (
                <option key={w.id} value={w.id}>
                  {w.code}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Desde</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div>
            <Label>Hasta</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {!productId ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Selecciona un producto para ver su kardex completo con saldos
            corridos.
          </CardContent>
        </Card>
      ) : reportQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : reportQuery.isError ? (
        <ErrorState
          title="No pudimos generar el reporte"
          description={reportQuery.error?.message ?? ""}
          onRetry={() => void reportQuery.refetch()}
          retryLabel="Reintentar"
        />
      ) : (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 text-sm">
            <div>
              <strong>{reportQuery.data!.productName}</strong>
              {reportQuery.data!.productSku && (
                <span className="ml-2 font-mono text-xs text-muted-foreground">
                  {reportQuery.data!.productSku}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="success">
                ↓ {fmtQty(reportQuery.data!.totals.inQty)} ·{" "}
                {fmtCop(reportQuery.data!.totals.inValue)}
              </Badge>
              <Badge variant="warning">
                ↑ {fmtQty(reportQuery.data!.totals.outQty)} ·{" "}
                {fmtCop(reportQuery.data!.totals.outValue)}
              </Badge>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Bodega</TableHead>
                <TableHead className="text-right">Cantidad</TableHead>
                <TableHead className="text-right">Costo unit.</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead className="text-right">Saldo qty</TableHead>
                <TableHead className="text-right">Saldo costo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reportQuery.data!.movements.map((m) => (
                <TableRow key={m.movementId}>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {fmtDateTime(m.createdAt)}
                  </TableCell>
                  <TableCell>
                    <MovementKindBadge kind={m.kind} short />
                  </TableCell>
                  <TableCell>
                    <Badge variant="muted" className="font-mono">
                      {m.warehouseCode}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className={`text-right tabular-nums ${
                      m.direction === "in"
                        ? "text-emerald-700 dark:text-emerald-400"
                        : "text-rose-700 dark:text-rose-400"
                    }`}
                  >
                    {m.direction === "in" ? "+" : "−"}
                    {fmtQty(m.qty)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {fmtCop2(m.unitCost)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmtCop(m.totalCost)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmtQty(m.qtyAfter)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {fmtCop2(m.avgCostAfter)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
